import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import {
  startOfLocalDay,
  startOfNextLocalDay,
  useLocalDayKey,
} from "@-/dev.improve.haniverse.lib.time.local-day";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";

export const GRAPHEMES_INTRODUCED_TODAY_TABLES = [
  "user_card",
  "user_grapheme_meaning_card",
] as const;

export function useGetGraphemesIntroducedTodayQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  // The count resets at local midnight with no table write to invalidate on, so the local day is
  // part of the key.
  const localDayKey = useLocalDayKey();
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(GRAPHEMES_INTRODUCED_TODAY_TABLES),
  );
  return () =>
    withTableChangeVersion(["graphemesIntroducedToday", { actorDb, localDayKey }] as const);
}

/**
 * How many distinct graphemes started studying on the current local day.
 *
 * A grapheme's meaning card is what marks its introduction: its pronunciation cards are enrolled
 * alongside it, and a word enrolling a later reading adds a pronunciation card to a grapheme that
 * was introduced before — neither is a NEW grapheme, so only meaning cards are counted.
 */
export const [useGraphemesIntroducedTodayQueryOptions, graphemesIntroducedTodayQueryFn] =
  createUseQueryOptions(
    useGetGraphemesIntroducedTodayQueryKey,
    async ({ queryKey: [_, { actorDb }] }) => {
      const now = new Date();
      // The local-day bounds must be computed at FETCH time (a midnight rollover between render
      // and fetch would count the wrong day), so this read can't be expressed as a render-built
      // `actorQueries` config; a bounded direct read is deliberate here.
      // oxlint-disable-next-line han-actor-db/no-raw-actor-db-in-query-fn -- see above
      const introducedCards = await actorDb(import.meta.location).query.userCard.findMany({
        columns: {},
        where: {
          fsrsCardId: { isNotNull: true },
          startedStudyingAt: {
            gte: startOfLocalDay(now).toISOString(),
            lt: startOfNextLocalDay(now).toISOString(),
          },
        },
        with: { userGraphemeMeaningCard: { columns: { grapheme: true } } },
      });
      const graphemes = new Set<string>();
      for (const card of introducedCards) {
        const grapheme = card.userGraphemeMeaningCard?.grapheme;
        if (grapheme !== undefined) {
          graphemes.add(grapheme);
        }
      }
      return graphemes.size;
    },
  );
